import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { site, nav } from "../data/content";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-[color:var(--paper)]/85 border-b border-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14">
        <div className="flex items-center justify-between py-4 md:py-5">
          {/* Logo (right in RTL) */}
          <Link
            to="/"
            data-testid="brand-logo"
            className="font-display font-black text-xl md:text-2xl tracking-tight hover:text-[color:var(--pink)] transition-colors"
          >
            {site.brandName}
          </Link>

          {/* Desktop nav (left in RTL) */}
          <nav className="hidden md:flex items-center gap-7 lg:gap-9 font-body">
            {nav.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 -m-2"
            aria-label="תפריט"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="md:hidden border-t border-black/10 bg-[color:var(--paper)]"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {nav.map((item) => (
                <NavLink
                  key={item.href}
                  item={item}
                  className="text-2xl font-display font-bold"
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function NavLink({ item, className = "" }) {
  // Hash links inside home use anchor; others use Link
  const isHash = item.href.includes("#");
  const base = `link-grow font-medium text-base lg:text-lg ${className}`;
  if (isHash) {
    return (
      <a href={item.href} data-testid={item.testId} className={base}>
        {item.label}
      </a>
    );
  }
  return (
    <Link to={item.href} data-testid={item.testId} className={base}>
      {item.label}
    </Link>
  );
}
